import React, { cloneElement, createContext, isValidElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Legend, Tooltip } from "recharts";

import { cn } from "../../lib/utils";

const ChartContext = createContext({});

function buildChartVars(config = {}) {
  return Object.entries(config).reduce((acc, [key, value]) => {
    if (value?.color) {
      acc[`--color-${key}`] = value.color;
    }
    return acc;
  }, {});
}

export function ChartContainer({ config = {}, className, children }) {
  const style = useMemo(() => buildChartVars(config), [config]);
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = Math.max(Math.floor(entry?.contentRect?.width || 0), 0);
      const height = Math.max(Math.floor(entry?.contentRect?.height || 0), 0);
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const canRenderChart = size.width > 0 && size.height > 0;

  return (
    <ChartContext.Provider value={config}>
      <div ref={containerRef} className={cn("w-full min-w-0", className)} style={style}>
        {canRenderChart ? (
          isValidElement(children)
            ? cloneElement(children, {
              width: size.width,
              height: size.height,
            })
            : children
        ) : (
          <div className="h-full w-full" aria-hidden="true" />
        )}
      </div>
    </ChartContext.Provider>
  );
}

export const ChartTooltip = Tooltip;
export const ChartLegend = Legend;

export function ChartTooltipContent({
  active,
  payload,
  label,
  labelFormatter,
  formatter,
  indicator = "dot",
  hideLabel = false,
  className,
}) {
  if (!active || !payload?.length) return null;

  const formattedLabel = labelFormatter ? labelFormatter(label) : label;

  return (
    <div
      className={cn("min-w-[180px] rounded-xl border px-3 py-2.5 text-left shadow-[0_20px_52px_-10px_rgba(0,0,0,0.9)]", className)}
      style={{
        backgroundColor: "hsl(var(--chart-tooltip-background))",
        borderColor: "hsl(var(--chart-tooltip-border))",
        opacity: 1,
      }}
    >
      {!hideLabel ? <p className="text-xs font-semibold text-popover-foreground">{formattedLabel}</p> : null}
      <div className={cn("space-y-1", hideLabel ? "" : "mt-2")}>
        {payload.map((entry) => {
          const dataKey = String(entry?.dataKey || "");
          const value = formatter ? formatter(entry?.value, entry?.name, entry) : entry?.value;
          const itemValue = Array.isArray(value) ? value[0] : value;
          const itemLabel = Array.isArray(value) ? value[1] : entry?.name;

          return (
            <div key={dataKey || String(entry?.name)} className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2">
                {indicator === "dot" ? (
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry?.color || entry?.stroke || "currentColor" }} />
                ) : null}
                <span className="text-muted-foreground">{itemLabel}</span>
              </div>
              <span className="font-semibold tabular-nums text-popover-foreground">{itemValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChartLegendContent({ payload = [] }) {
  const config = useContext(ChartContext);

  if (!payload.length) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {payload.map((entry) => {
        const dataKey = String(entry?.dataKey || "");
        const label = config?.[dataKey]?.label || entry?.value || dataKey;
        const color = entry?.color || config?.[dataKey]?.color;

        return (
          <div key={dataKey} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span>{label}</span>
          </div>
        );
      })}
    </div>
  );
}
